import { ReactNode } from 'react';
import {
	BookingPricingResult,
	IPriceLineItem,
	PricingResultKey,
} from 'Services/Api/_HumanWritten/PricingService/PricingResult';

export interface ClassLegItem {
	label: ReactNode;
	price: number;
}

export interface ClassLegGroup {
	/** Stable across renders: the fixed groups' names, or `class:<name>` for a fare type. */
	key: string;
	name: ReactNode;
	items: ClassLegItem[];
}

/** The headings the leg supplies itself. Rendered through `FormattedMessage`, so a client can reword them. */
export interface ClassLegGroupNames {
	vehicle: ReactNode;
	trailer: ReactNode;
	passengers: ReactNode;
	addOns: ReactNode;
	other: ReactNode;
	alterationFee: ReactNode;
	/** The vehicle's or trailer's own fare. `band` is what was chosen — `medium`, `medium, 1500 kg`. */
	baseFare: (band?: string) => ReactNode;
	extraLength: (band?: string) => ReactNode;
	extraWeight: (band?: string) => ReactNode;
	extraWidth: ReactNode;
	/** A trailer taken off the booking by an edit. */
	removed: (band?: string) => ReactNode;
}

export interface ClassLeg {
	groups: ClassLegGroup[];
	/** What the leg is worth — the server's total, less the fees the totals card lists instead. */
	total: number;
	/** Discounts on this leg, shown beside its total rather than as a line of their own. */
	discount: number;
}

// Width is one combined line for the vehicle, its trailer and the driver, so it stays with the vehicle.
const vehicleKeys: PricingResultKey[] = [
	'BaseCargo',
	'AdditionalCargoLength',
	'AdditionalCargoWeight',
	'AdditionalCargoWidth',
	'TotalCargo',
];

const trailerKeys: PricingResultKey[] = ['BaseTowOn', 'AdditionalTowOnLength', 'AdditionalTowOnWeight', 'TotalTrailer'];

/** Extras a new booking does not show at $0 — the smallest band. An edit keeps them, as "no change". */
const freeExtraKeys: PricingResultKey[] = [
	'AdditionalCargoLength',
	'AdditionalCargoWeight',
	'AdditionalCargoWidth',
	'AdditionalTowOnWeight',
];

const addOnKeys: PricingResultKey[] = ['AddOn', 'SeatSelectionFee'];

/**
 * Not shown on the leg at all. The trailer removal, check-in forfeit and general removal fees are
 * presented once, in the totals card under the subtotal, and are taken back off the leg total below —
 * none is part of what the trip is worth (see `BookingBreakdown`). The ticket price increase is a
 * display-only line for the manager's waive button. Freight never reaches this summary.
 */
const hiddenKeys: PricingResultKey[] = [
	'TowOnCancellationFee',
	'CheckInForfeitFee',
	'RemovalFee',
	'TicketPriceIncrease',
	'Discount',
	'FreightBase',
	'FreightHandling',
];

/** The chosen band from a server description: `Vehicle size (medium, 1500 kg)` → `medium, 1500 kg`. */
export const bandOf = (description?: string): string | undefined =>
	description?.match(/\(([^()]*)\)(?:\s*-\s*removed)?\s*$/)?.[1]?.trim() || undefined;

const isRemoved = (line: IPriceLineItem) => /-\s*removed\s*$/.test(line.description ?? '');

/**
 * The server's descriptions repeat the noun the group heading already gives (`1 vehicle`, `Vehicle size
 * (medium)`), so each line is named for what it charges, keeping the band the booker chose. A $0 extra on
 * a new booking is dropped and its band moves onto the base fare, so the choice is still shown.
 */
function cargoItems(
	lines: IPriceLineItem[],
	keys: PricingResultKey[],
	names: ClassLegGroupNames,
	isAlteration: boolean,
): ClassLegItem[] {
	const own = lines.filter(x => keys.includes(x.key));
	const hidden = own.filter(x => !isAlteration && freeExtraKeys.includes(x.key) && x.price === 0);
	const hiddenBands = hidden
		.filter(x => x.key === 'AdditionalCargoLength')
		.map(x => bandOf(x.description))
		.filter((x): x is string => !!x);

	return own
		.filter(x => !hidden.includes(x))
		.map(x => {
			const band = bandOf(x.description);
			switch (x.key) {
				case 'BaseCargo':
					return { label: names.baseFare(hiddenBands.join(', ') || undefined), price: x.price };
				case 'BaseTowOn':
					return { label: names.baseFare(), price: x.price };
				case 'AdditionalTowOnLength':
					// The trailer's whole fare at its length; there is no base line beneath it.
					return { label: isRemoved(x) ? names.removed(band) : names.baseFare(band), price: x.price };
				case 'AdditionalCargoLength':
					return { label: names.extraLength(band), price: x.price };
				case 'AdditionalCargoWeight':
				case 'AdditionalTowOnWeight':
					return { label: names.extraWeight(band), price: x.price };
				case 'AdditionalCargoWidth':
					return { label: names.extraWidth, price: x.price };
				default:
					return { label: x.description?.trim() || names.vehicle, price: x.price };
			}
		});
}

const sum = (items: IPriceLineItem[], key: PricingResultKey) =>
	items.filter(x => x.key === key).reduce((total, x) => total + x.price, 0);

/**
 * One leg's price lines, grouped for the summary: a group per fare type in the order the server gave
 * them (or one `Passengers` group), then the vehicle, then its trailer, then add-ons and seat fees, then anything else.
 *
 * The passenger rows come from `passengerClassLines`, which the server only sends when they add up to
 * the passenger line. When it sends none, the passenger line is shown whole under a plain heading, so
 * the leg still adds up to its total.
 */
export function classLegGroups(
	bookingInfo: BookingPricingResult,
	names: ClassLegGroupNames,
	isAlteration = false,
): ClassLeg {
	const lines = bookingInfo.priceLineItems;
	const groups: ClassLegGroup[] = [];

	const classLines = bookingInfo.passengerClassLines ?? [];
	if (classLines.length > 0) {
		classLines.forEach(line => {
			const name = line.className ?? names.passengers;
			const key = line.className ? `class:${line.className}` : 'passengers';
			let group = groups.find(g => g.key === key);
			if (!group) {
				group = { key, name, items: [] };
				groups.push(group);
			}
			// A server older than the class split sends no label; its description still names the row.
			group.items.push({ label: line.label ?? line.description, price: line.price });
		});
	} else if (lines.some(x => x.key === 'Passenger')) {
		const passenger = lines.find(x => x.key === 'Passenger')!;
		groups.push({
			key: 'passengers',
			name: names.passengers,
			items: [{ label: passenger.description, price: sum(lines, 'Passenger') }],
		});
	}

	const vehicle = cargoItems(lines, vehicleKeys, names, isAlteration);
	if (vehicle.length > 0) {
		groups.push({ key: 'vehicle', name: names.vehicle, items: vehicle });
	}

	const trailer = cargoItems(lines, trailerKeys, names, isAlteration);
	if (trailer.length > 0) {
		groups.push({ key: 'trailer', name: names.trailer, items: trailer });
	}

	const addOns = lines.filter(x => addOnKeys.includes(x.key)).map(x => ({ label: x.description, price: x.price }));
	if (addOns.length > 0) {
		groups.push({ key: 'add-ons', name: names.addOns, items: addOns });
	}

	const other = lines
		.filter(
			x =>
				x.key !== 'Passenger' &&
				!vehicleKeys.includes(x.key) &&
				!trailerKeys.includes(x.key) &&
				!addOnKeys.includes(x.key) &&
				!hiddenKeys.includes(x.key),
		)
		.map(x => ({
			// The server's alteration fee line reads "Alteration fee:", written for a label/value layout.
			label: x.key === 'AlterationFee' ? names.alterationFee : x.description,
			price: x.price,
		}));
	if (other.length > 0) {
		groups.push({ key: 'other', name: names.other, items: other });
	}

	return {
		groups,
		total:
			bookingInfo.totalPrice -
			sum(lines, 'TowOnCancellationFee') -
			sum(lines, 'CheckInForfeitFee') -
			sum(lines, 'RemovalFee'),
		discount: sum(lines, 'Discount'),
	};
}
